import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import './App.css';
import Navbar from './Navbar';
import About from './About';
import Services from './Services';
import { addUserFromContactForm, sendEmailToSelf } from './firebase';
import { parsePhoneNumberWithError } from 'libphonenumber-js';
import { buildContactEvent, sendFacebookServerEvent } from './fbConversions';
import GoogleReviewsButton from './GoogleReviewsButton';

const firePixelEvent = (eventName, params) => {
  if (typeof window !== 'undefined' && window.fbq && typeof window.fbq === 'function') {
    window.fbq('track', eventName, params);
  }
};

const getCookieValue = (name) => {
  if (typeof document === 'undefined') return null;
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
  return null;
};

function PixelRouteTracker() {
  const location = useLocation();
  const firstPageLoad = useRef(true);

  useEffect(() => {
    if (firstPageLoad.current) {
      firstPageLoad.current = false;
      return;
    }
    firePixelEvent('PageView');
    firePixelEvent('ViewContent', { content_name: location.pathname });
  }, [location]);

  return null;
}
// import CostEstimator from './CostEstimator';
// import Reviews from './Reviews';
// import { collection, addDoc } from "firebase/firestore";
// import ChatBot from './ChatBot';

function MainPage() {
  // const [chatVisible, setChatVisible] = useState(false);
  // const handleOpenChat = () => setChatVisible(true);
  // const handleCloseChat = () => setChatVisible(false);

  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');


  // Sanitize input to prevent XSS/SQLi
  function sanitizeInput(str) {
    if (!str) return '';
    return str.replace(/[<>"'`;\\]/g, '')
      .replace(/(select|insert|update|delete|drop|union|--|#|\*|\bOR\b|\bAND\b)/gi, '');
  }

  function validatePhoneNumber(phoneNumberInput) {
    // Use 'US' as the default country code if none is provided
    try {
      const phoneNumber = parsePhoneNumberWithError(phoneNumberInput, 'US');
      // Check if the parsed object is valid
      if (phoneNumber) {
        return phoneNumber.isValid();
      }
      // Return false if the input couldn't be parsed at all
      return false;

    } catch (error) {
      return false;
    }
  }

  function validateEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  // Simple in-memory rate limit (per session)
  const [lastSubmit, setLastSubmit] = useState(0);
  const [submitCount, setSubmitCount] = useState(0);

  const handleContactSubmit = async (e) => {
    e.preventDefault();

    // Rate limit: max 1 per 30s, max 5 per hour (per session)
    const now = Date.now();
    if (now - lastSubmit < 30000) {
      alert('Please wait 30 seconds before submitting again.');
      return;
    }
    if (submitCount >= 5) {
      alert('You have reached the maximum number of submissions for this hour.');
      return;
    }

    // Sanitize all inputs
    const safeName = sanitizeInput(name);
    const safePhone = sanitizeInput(phone);
    const safeEmail = sanitizeInput(email);
    const safeMessage = sanitizeInput(message);

    if (!safeName || !safePhone || !safeEmail || !safeMessage) {
      alert('Please fill out all fields before submitting.');
      return;
    }

    if (!validatePhoneNumber(safePhone)) {
      alert('Please enter a valid phone number.');
      return;
    }

    if (!validateEmail(safeEmail)) {
      alert('Please enter a valid email address.');
      return;
    }

    try {
      await addUserFromContactForm(safeName, safeEmail, safePhone, safeMessage);
      alert('Your message has been sent! We will reach out to you ASAP.');

      await sendEmailToSelf(safeName, safeEmail, safePhone, safeMessage);
      firePixelEvent('Lead');

      const eventPayload = await buildContactEvent({
        name: safeName,
        email: safeEmail,
        phone: safePhone,
        pageUrl: window.location.href,
        userAgent: navigator.userAgent,
        fbp: getCookieValue('_fbp'),
        fbc: getCookieValue('_fbc'),
        message: safeMessage,
      });

      sendFacebookServerEvent(eventPayload).catch((err) => {
        console.error('Facebook Conversions API error:', err);
      });

      setName('');
      setEmail('');
      setPhone('');
      setMessage('');
      setLastSubmit(now);
      setSubmitCount(c => c + 1);

      // Reset count after 1 hour
      setTimeout(() => setSubmitCount(0), 3600000);
    } catch (error) {
      console.error('Error adding document: ', error);
      alert('Failed to send message. Please try again.');
    }
  };

  return (
    <div className="App">
      <Navbar />
      <div className="main-content">
        <header className="hero-section" id="hero">
          {/* <img src="/images/pullup.png" alt="Pull Up Exercise" /> */}
          {/* <h1>Boun Health & Performance</h1> */}
          <p>Transform your body and mind with expert guidance.</p>
          <br />

          <section className="services-section" id="services">
            <h2>SERVICES</h2>
            <ul>
              <li>1-on-1 Personal Training</li>
              <li>Online Training Programs</li>
              <li>Beginner-Friendly Programs</li>
              <li>Injury-Conscious Programs</li>
            </ul>
          </section>

          {/* <a href="#contact" className="cta-button">Book a Free Consultation</a> */}

          <section className="contact-section" id="contact">
            <h2>Book a Consultation</h2>
            <form className="contact-form">
              <input
                type="text"
                placeholder="Your Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
              <input
                type="text"
                placeholder="Your Phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                required
              />
              <input
                type="email"
                placeholder="Your Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
              <textarea
                placeholder="How can I help you?"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                required
              ></textarea>
              <button type="submit" onClick={handleContactSubmit}>Send Message</button>
            </form>
          </section>

        </header>

        <section className="testimonials-section">
          <h2>Client Testimonials <GoogleReviewsButton /></h2>
          {/* <a href="https://maps.app.goo.gl/GYRSQRGPNLTzfaFXA">Click here for Google Maps Reviews</a> */}

          {/* <div id="featurable-280d7cbe-8bec-4bdd-9133-095f81b1a998" data-featurable-async ></div> */}
          {/* <script src="https://featurable.com/assets/bundle.js" defer charset="UTF-8"></script> */}
          {/* <div id="featurable-ccf1bb79-dcd2-42ca-9235-1780f54a134a" data-featurable-async ></div><script src="https://featurable.com/assets/bundle.js" defer charset="UTF-8"></script> */}
          {/* <iframe title="Google Reviews" src="https://www.featurable.com/widgets/280d7cbe-8bec-4bdd-9133-095f81b1a998" style={{ width: '100%', height: '600px', border: 'none' }}></iframe> */}
          {/* <iframe title="Google Reviews" src="https://www.featurable.com/widgets/ccf1bb79-dcd2-42ca-9235-1780f54a134a" style={{ width: '100%', height: '600px', border: 'none' }}></iframe> */}
          <iframe title="Google Reviews" src='https://widgets.sociablekit.com/google-reviews/iframe/25604112' frameborder='0' width='100%' height='1000'></iframe>
          {/* <Reviews /> */}
        </section>

        <footer className="footer">
          <p>&copy; {new Date().getFullYear()} Boun Health & Performance</p>
        </footer>
      </div>
    </div>
  );
}

function App() {
  return (
    <Router>
      <PixelRouteTracker />
      <Routes>
        <Route path="/about" element={<About />} />
        <Route path="/" element={<MainPage />} />
        <Route path="/services" element={<Services />} />
        {/* <Route path="/cost-estimator" element={<CostEstimator />} /> */}
      </Routes>
    </Router>
  );
}

export default App;
